import { useState, useCallback, useRef, useEffect } from "react"

type AudioRecordingStatus =
  | "inactive"
  | "recording"
  | "paused"
  | "processing"
  | "error"
type AudioRecordingError =
  | "permission-denied"
  | "not-supported"
  | "unknown"
  | null

// Helper function to get file extension from MIME type
const getFileExtensionFromMimeType = (mimeType: string): string => {
  if (mimeType.includes("webm")) return "webm"
  if (mimeType.includes("mp4") || mimeType.includes("m4a")) return "m4a"
  if (mimeType.includes("wav")) return "wav"
  if (mimeType.includes("ogg")) return "ogg"
  return "webm" // default fallback
}

interface UseRecordAudioMessageReturn {
  isRecording: boolean
  isPaused: boolean
  isProcessing: boolean
  hasError: boolean
  status: AudioRecordingStatus
  error: AudioRecordingError
  audioStream: MediaStream | null
  recordedAudioUrl: string | null
  startRecording: () => Promise<void>
  pauseRecording: () => void
  resumeRecording: () => void
  stopRecording: () => void
  cancelRecording: () => void
  clearError: () => void
  createPreviewForPlayback: () => Promise<string | null>
}

export default function useRecordAudioMessage(): UseRecordAudioMessageReturn {
  const [status, setStatus] = useState<AudioRecordingStatus>("inactive")
  const [error, setError] = useState<AudioRecordingError>(null)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const mimeTypeRef = useRef<string>("")
  const isCancelledRef = useRef<boolean>(false)

  const cleanupAudioStream = useCallback(() => {
    // Use a ref to get the current audio stream to avoid closure issues
    setAudioStream((currentStream) => {
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop())
      }
      return null
    })
  }, [])

  const createPreviewUrl = useCallback(() => {
    // For now, just set a placeholder URL to indicate preview is available
    // The actual playback will be handled differently
    if (audioChunksRef.current.length > 0) {
      setRecordedAudioUrl("preview-available")
      return "preview-available"
    }
    return null
  }, [])

  const cleanupPreviewUrl = useCallback(() => {
    if (recordedAudioUrl) {
      if (recordedAudioUrl !== "preview-available") {
        URL.revokeObjectURL(recordedAudioUrl)
      }
      setRecordedAudioUrl(null)
    }
  }, [recordedAudioUrl])

  const createPreviewForPlayback = useCallback(async (): Promise<
    string | null
  > => {
    if (audioChunksRef.current.length === 0) {
      return null
    }

    try {
      // Get the current accumulated chunks and request any pending data
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "paused"
      ) {
        // Request any remaining data from the recorder
        mediaRecorderRef.current.requestData()

        // Wait a bit for the data to be available
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      const actualMimeType = mimeTypeRef.current || "audio/webm"

      // Create blob from all accumulated chunks
      const previewBlob = new Blob(audioChunksRef.current, {
        type: actualMimeType,
      })

      if (previewBlob.size > 0) {
        const url = URL.createObjectURL(previewBlob)
        return url
      }

      return null
    } catch (error) {
      console.error("Failed to create preview from chunks:", error)
      return null
    }
  }, [])

  const startRecording = useCallback(async () => {
    audioChunksRef.current = []
    isCancelledRef.current = false
    // Clear any previous errors and preview URL
    setError(null)
    cleanupPreviewUrl()
    // Set status to recording immediately to show the modal
    setStatus("recording")

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      setAudioStream(stream)

      // Determine the best supported audio format - prefer non-WebM formats
      let mimeType = ""

      // Try MP4/AAC first (best for WhatsApp compatibility)
      const preferredFormats = [
        "audio/mp4;codecs=mp4a.40.2", // AAC-LC
        "audio/mp4", // Generic MP4
        "audio/mpeg", // MP3
        "audio/wav", // WAV
        "audio/ogg;codecs=opus", // OGG Opus
        "audio/webm;codecs=opus", // WebM Opus (fallback)
        "audio/webm", // Generic WebM (last resort)
      ]

      for (const format of preferredFormats) {
        if (MediaRecorder.isTypeSupported(format)) {
          mimeType = format
          break
        }
      }

      // If no formats are supported, let browser choose
      if (!mimeType) {
        mimeType = ""
      }

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType })
      mimeTypeRef.current = mimeType

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data)
      }

      mediaRecorderRef.current.onstop = async () => {
        // If recording was cancelled, don't process anything
        if (isCancelledRef.current) {
          setStatus("inactive")
          return
        }

        setStatus("processing")

        try {
          // Only process if we have audio data
          if (audioChunksRef.current.length > 0) {
            const actualMimeType = mimeTypeRef.current || "audio/webm"
            const fileExtension = getFileExtensionFromMimeType(actualMimeType)

            const audioBlob = new Blob(audioChunksRef.current, {
              type: actualMimeType,
            })

            // Only proceed if the audio is longer than 0.5 seconds
            if (audioBlob.size > 1000) {
              // Create a File object for the voice message with correct extension
              const voiceFile = new File(
                [audioBlob],
                `voice-message.${fileExtension}`,
                {
                  type: actualMimeType,
                },
              )

              // Dispatch event with the audio file instead of transcription
              window.dispatchEvent(
                new CustomEvent("voice-message-complete", {
                  detail: { file: voiceFile },
                }),
              )
            }
          }

          setStatus("inactive")
        } catch (error) {
          console.error("Voice message error:", error)
          setStatus("error")
        }

        // Stop tracks immediately after processing is complete
        // Use the stream variable from the current scope, not the closure
        stream.getTracks().forEach((track) => track.stop())
        setAudioStream(null)
      }

      mediaRecorderRef.current.start(1000) // Request data every 1000ms
    } catch (error) {
      console.error("Error accessing microphone:", error)

      setStatus("error")

      // Clean up any partial audio stream that might have been created
      cleanupAudioStream()

      // Determine the type of error
      if (
        (error as Error).name === "NotAllowedError" ||
        (error as Error).name === "PermissionDeniedError"
      ) {
        setError("permission-denied")
      } else if (
        (error as Error).name === "NotSupportedError" ||
        (error as Error).name === "NotFoundError"
      ) {
        setError("not-supported")
      } else {
        setError("unknown")
      }
    }
  }, [cleanupAudioStream])

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === "recording") {
      // Request current data before pausing
      mediaRecorderRef.current.requestData()
      mediaRecorderRef.current.pause()
      setStatus("paused")
      // Create preview URL when pausing (after a small delay to ensure data is available)
      setTimeout(() => {
        createPreviewUrl()
      }, 100)
    }
  }, [status, createPreviewUrl])

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === "paused") {
      mediaRecorderRef.current.resume()
      setStatus("recording")
    }
  }, [status])

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      (status === "recording" || status === "paused")
    ) {
      mediaRecorderRef.current.stop()
    }
    // Note: Don't stop tracks here - let the MediaRecorder's onstop handler do the cleanup
    // This allows the recording to be processed properly
  }, [status])

  const cancelRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      (status === "recording" || status === "paused")
    ) {
      // Mark as cancelled to prevent processing in onstop handler
      isCancelledRef.current = true
      // Clear audio chunks to prevent processing
      audioChunksRef.current = []
      mediaRecorderRef.current.stop()
    }
    // Immediately clean up and set status
    setStatus("inactive")
    cleanupAudioStream()
    cleanupPreviewUrl()
  }, [status, cleanupAudioStream, cleanupPreviewUrl])

  const clearError = useCallback(() => {
    setError(null)
    setStatus("inactive")
    cleanupAudioStream()
  }, [cleanupAudioStream])

  // Cleanup effect to ensure microphone is always released on unmount only
  useEffect(() => {
    return () => {
      // Clean up on unmount only
      setAudioStream((currentStream) => {
        if (currentStream) {
          currentStream.getTracks().forEach((track) => track.stop())
        }
        return null
      })
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop()
      }
    }
  }, []) // Empty dependency array - only runs on mount/unmount

  return {
    isRecording: status === "recording",
    isPaused: status === "paused",
    isProcessing: status === "processing",
    hasError: status === "error",
    status,
    error,
    audioStream,
    recordedAudioUrl,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    clearError,
    createPreviewForPlayback,
  }
}
